import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import rentsService from '../services/rentsService';

const StartRent = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({
    date_start: '',
    date_end: ''
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStartRentData = async () => {
      try {
        setLoading(true);

        // Laravel equivalent: GET /rents/{rentId}/start -> RentsController@setFinal
        const result = await rentsService.getStartRentData(rentId);
        setData(result);

        // Initialize form data with existing dates
        setFormData(prev => ({
          ...prev,
          date_start: result.rent.date_start ? result.rent.date_start.split('T')[0] : new Date().toISOString().split('T')[0],
          date_end: result.rent.date_end ? result.rent.date_end.split('T')[0] : ''
        }));

      } catch (err) {
        console.error('Error fetching start rent data:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch start rent data');
      } finally {
        setLoading(false);
      }
    };

    if (rentId) {
      fetchStartRentData();
    }
  }, [rentId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Convert ISO date (YYYY-MM-DD) to Laravel format (d/m/Y)
  const convertToDMY = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.permissions?.includes('rents.start')) {
      toast.error("You don't have permission to start rents");
      return;
    }

    if (!formData.date_start) {
      toast.error("Start date is required");
      return;
    }

    try {
      setSaving(true);

      // Laravel Parity: Convert ISO format to d/m/Y format before sending
      const submitData = {
        date_start: convertToDMY(formData.date_start),
        date_end: formData.date_end ? convertToDMY(formData.date_end) : undefined,
      };

      console.log("📤 [FRONTEND] Sending dates in Laravel format:", submitData);

      // Laravel equivalent: POST /rents/{rentId}/start -> RentsController@start
      const result = await rentsService.startRent(rentId, submitData);
      toast.success(result.message || 'Rent started successfully!');

      // Navigate to guardian show page (matching Laravel behavior)
      navigate(`/guardians/${data.guardian.id}`);

    } catch (err) {
      console.error('Error starting rent:', err);
      
      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        toast.error(errorMessages[0] || "Validation failed");
      } else {
        toast.error(err.response?.data?.message || err.message || 'Failed to start rent');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;
  if (!data) return <div className="alert alert-warning">Data not found</div>;

  return (
    <div className="container">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/guardians">Guardians</a>
          </li>
          <li className="breadcrumb-item">
            <a href={`/guardians/${data.guardian.id}`}>{data.guardian.full_name}</a>
          </li>
          <li className="breadcrumb-item active">Start Rent</li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard">
            <div className="col-md-7">
              <h3>Guardian:</h3><br/>
              <a href={`/guardians/${data.guardian.id}`}>
                {data.guardian.full_name} ({data.guardian.unique_guardian_number})
              </a>
              <br/>
              <h3>Property application process:</h3><br/>
              <a href={`/properties/${data.property.id}`}>
                <p className="mb-0">{data.property.address_1}</p>
                {data.property.address_2 && <p className="mb-0">{data.property.address_2}</p>}
                {data.property.address_3 && <p className="mb-0">{data.property.address_3}</p>}
              </a>
            </div>
            <div className="col-md-5">
              <h3>Property date take on:</h3><br/>
              {data.property.date_taken_on ? new Date(data.property.date_taken_on).toLocaleDateString() : 'Not set'}
              <br/>
              <h3>Property date take back:</h3><br/>
              {data.property.date_taken_back ? new Date(data.property.date_taken_back).toLocaleDateString() : 'Not set'}
            </div>
          </div>

          <div className="panel-group border-down margin-down">
            {/* License Agreements Section */}
            <div className="row">
              <div className="col-xs-6">
                {data.rent.license_agreement && (
                  <div className="property-application-license-agreement">
                    <div className="btn-property">
                      <div className="document-left">
                        <img src="/assets/images/pdf.png" alt="PDF" />
                      </div>
                      <div className="document-right">
                        <p className="btn-title">Agreement</p>
                        <p className="btn-short">Click to open & print</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-xs-6">
                {data.rent.signed_license_agreement && (
                  <div className="property-application-license-agreement">
                    <div className="btn-property">
                      <div className="document-left">
                        <img src="/assets/images/pdf.png" alt="PDF" />
                      </div>
                      <div className="document-right">
                        <p className="btn-title">Signed Agreement</p>
                        <p className="btn-short">Click to open & print</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="clearfix"></div>
            </div>

            {/* Start Rent Form */}
            <form onSubmit={handleSubmit} className="mt-30">
              <div className="row">
                <div className="col-xs-push-3 col-xs-6">
                  <div className="rent-set-final">
                    <div className="rent-set-final-header">Set the dates</div>
                    <div className="rent-set-final-body">
                      <fieldset className="form-horizontal">
                        <div className="form-group">
                          <label htmlFor="date_start" className="control-label col-sm-5 label-required">
                            Date Start *
                          </label>
                          <div className="col-sm-4">
                            <input
                              type="date"
                              className="form-control"
                              id="date_start"
                              name="date_start"
                              value={formData.date_start}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="date_end" className="control-label col-sm-5">
                            Date End
                          </label>
                          <div className="col-sm-4">
                            <input
                              type="date"
                              className="form-control"
                              id="date_end"
                              name="date_end"
                              value={formData.date_end}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group text-right">
                <button
                  type="submit"
                  className="btn btn-head btn-standard"
                  disabled={saving}
                >
                  {saving ? 'Starting rent...' : 'Start rent'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary ml-10"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartRent;